// stores/dashboardStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from './userStore'
import * as companyApi from '@/api/companyApi.js'
import * as projectApi from '@/api/projectApi.js'
import * as dashboardApi from '@/api/dashboardApi.js'

const SPACE_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DELETED: 'deleted',
}

const ERROR_REQUEST = {
  CANT_MOVE_TO_TRASH: 35
}

export const useDashboardStore = defineStore('dashboard', () => {
  // State
  const spaces = ref([])
  const activeSpaces = ref([])
  const stats = ref([])
  const status = ref(SPACE_STATUS.ACTIVE)
  const loading = ref(false)
  const error = ref(null)
  const _authStore = ref(null)
  const _initialized = ref(false)

  // Getters
  const isLoading = computed(() => loading.value)
  const getError = computed(() => error.value)
  const statsInfo = computed(() => stats.value)
  const spacesInfo = computed(() => spaces.value)
  const activeSpacesInfo = computed(() => activeSpaces.value)
  const isInitialized = computed(() => _initialized.value)
  const isAuthStoreReady = computed(() => _authStore.value && _authStore.value.token !== null)

  async function waitForCondition(condition, timeout = 3000, interval = 100) {
    const start = Date.now()

    while (Date.now() - start < timeout) {
      if (condition()) return true
      await new Promise(resolve => setTimeout(resolve, interval))
    }
    
    _initialized.value = false

    throw new Error('Condition not met within timeout')
  }

  // Actions
  async function init() {
    if (_initialized.value) return
    
    _authStore.value = useUserStore()
    
    try {
      await waitForCondition(() => isAuthStoreReady.value)

      loading.value = true

      await Promise.all([
        fetchSpaces(),
        fetchUserStats()
      ])

      _initialized.value = true
    } catch (err) {
      error.value = err
      _initialized.value = false
      throw err
    } finally {
      loading.value = false
    }
  }

  async function ensureAuthStore() {
    if (!_authStore.value) {
      _authStore.value = useUserStore()
    }
    await waitForCondition(() => isAuthStoreReady.value)
  }

  async function setStatus(newStatus) {
    if (!Object.values(SPACE_STATUS).includes(newStatus)) {
      throw new Error(`Invalid status: ${newStatus}`)
    }
    status.value = newStatus
    await fetchSpaces()
  }

  async function fetchSpaces() {   
    try {
      await ensureAuthStore()

      loading.value = true
      let response = []

      activeSpaces.value = await dashboardApi.listSpaces(_authStore.value.token)

      switch (status.value) {
        case SPACE_STATUS.ACTIVE:
          spaces.value = activeSpaces.value
          break
        case SPACE_STATUS.ARCHIVED:
          response = await dashboardApi.listArchivedSpaces(_authStore.value.token)
          spaces.value = response
          break
        case SPACE_STATUS.DELETED:
          response = await dashboardApi.listDeletedSpaces(_authStore.value.token)
          spaces.value = response
          break
        default:
          throw new Error('Invalid status')
      }
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchUserStats() {   
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await dashboardApi.userStats(_authStore.value.token)
      stats.value = response
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateSpace(data) {    
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await companyApi.updateCompany(_authStore.value.token, data)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archiveSpace(id, all = null) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await companyApi.archCompany(_authStore.value.token, id, all)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archivedSpaceRestore(id, all = null) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await companyApi.archCompanyRestore(_authStore.value.token, id, all)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function trashSpace(id, all = null) {    
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await companyApi.trashCompany(_authStore.value.token, id, all)
      
      if (response.code && response.code == ERROR_REQUEST.CANT_MOVE_TO_TRASH) {
        return false
      } else {
        await fetchSpaces()
        return response
      }
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function trashedSpaceRestore(id, all = null) {    
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await companyApi.trashCompanyRestore(_authStore.value.token, id, all)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      if (err.code == 35) {

      }
      throw err
    } finally {
      loading.value = false
    }
  }  

  async function deleteSpace(id) {    
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await companyApi.delCompany(_authStore.value.token, id)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function projectInfo(id) {    
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await dashboardApi.projectInfo(_authStore.value.token, id)
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function projectTeam(id) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await dashboardApi.projectTeam(_authStore.value.token, id)
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archiveProject(id) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await projectApi.archProject(_authStore.value.token, id)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archivedProjectRestore(id) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await projectApi.archProjectRestore(_authStore.value.token, id)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  async function trashProject(id) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await projectApi.trashProject(_authStore.value.token, id)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      return false
    } finally {
      loading.value = false
    }
  }

  async function trashedProjectRestore(id) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await projectApi.trashProjectRestore(_authStore.value.token, id)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      return false
    } finally {
      loading.value = false
    }
  }


  async function deleteProject(id) {
    try {
      await ensureAuthStore()

      loading.value = true
      const response = await projectApi.delProject(_authStore.value.token, id)
      await fetchSpaces()
      return response
    } catch (err) {
      error.value = err
      return false
    } finally {
      loading.value = false
    }
  }

  async function refresh() {
    try {
      loading.value = true

      await fetchSpaces()
      await fetchUserStats()

    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }    
  }

  async function reset() {
      spaces.value  = [];
      stats.value   = [];
      status.value  = SPACE_STATUS.ACTIVE;
      loading.value = false;
      error.value   = null;
      _authStore.value = null;
      _initialized.value = false;
  }

  return {
    // Const
    SPACE_STATUS,
    
    // State
    spaces,
    activeSpacesInfo,
    stats,
    status,
    loading,
    error,
    
    // Getters
    isLoading,
    statsInfo,
    spacesInfo,
    getError,
    isInitialized,
    
    // Actions
    init,
    setStatus,
    fetchSpaces,
    fetchUserStats,
    updateSpace,
    archiveSpace,
    archivedSpaceRestore,
    trashSpace,
    trashedSpaceRestore,
    deleteSpace,
    projectInfo,
    projectTeam,
    archiveProject,
    archivedProjectRestore,
    trashProject,
    trashedProjectRestore,
    deleteProject,
    reset,
    refresh
  }
})